import type { SpatialRectangle } from "@pdfking/renderer"
import { PlotPage } from "@pdfking/renderer"
import { IterableToArray } from "@pdfking/tfunc"

import { GenerateFile } from "../../files/GenerateFile"
import { RequestBlob } from "../../files/RequestBlob"
import type { PdfEngine } from "../../types/engine/PdfEngine"
import { ContentType } from "../../types/files/ContentType"
import { VirtualFileType } from "../../types/files/VirtualFileType"
import type { TaskResult } from "../../types/tasks/input/TaskResult"
import type { TaskPlotOptions } from "../../types/tasks/options/TaskPlotOptions"
import { TaskName } from "../../types/tasks/TaskName"
import { TrackedTaskName } from "../../types/tasks/tracking/TrackedTaskName"
import type { TrackingCallback } from "../../types/tasks/tracking/TrackingCallback"

// options PlotPdfOptions - file: Plot
// stream -
// emit TrackingCallback
//
// return DocumentPage[page, GeneratedFile[]] // takes the page number from PlotPdfOptions

export async function* TaskPlot(
  engine: PdfEngine,
  task: TaskPlotOptions,
  emit: TrackingCallback
): AsyncGenerator<TaskResult> {
  const emitter = (done: number) =>
    emit({
      name: TrackedTaskName.PLOT_PDF,
      done,
      total: 1,
    })

  void emitter(0)
  const content = await GenerateFile(
    engine,
    [
      await PlotPage(task.page, {
        unit_to_px: task.options.unit_to_px,
        stroke_width: task.options.stroke_width,
        scale_bleed: task.options.scale_bleed,
        ...task.context,
        file_map: Object.fromEntries(
          Object.entries(task.context.file_map || {}).map(([k, file]) => [
            k,
            RequestBlob(engine, file)
              .then(info => IterableToArray(info.chunks))
              .then(blobs => new Blob(blobs)),
          ])
        ),
      }),
    ],
    ContentType.PDF
  )

  const box: SpatialRectangle = {
    x: 0,
    y: 0,
    width: task.page.width,
    height: task.page.height,
  }

  yield {
    enum: TaskName.CONTENT_FILE,
    name: "plot.pdf",
    content: {
      enum: VirtualFileType.SINGLE_PAGE_PDF,
      content,
      source: undefined,
      page: task.page,
      context: task.context,
      page_number: 1,
      page_count: 1,
      file_meta: {},
      trim_box: box,
      media_box: box,
    },
  }

  void emitter(1)
}
